import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import { DashboardStats } from '@/types'
import {
  UsersIcon,
  UserGroupIcon,
  BanknotesIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'

export default function DashboardPage() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => apiClient.get<DashboardStats>('/admin-dashboard-stats'),
    retry: false,
  })

  const statsCards = [
    {
      name: '总用户数',
      value: stats?.data?.total_users || 0,
      change: '+12%',
      changeType: 'positive' as const,
      icon: UsersIcon,
    },
    {
      name: '活跃用户',
      value: stats?.data?.active_users || 0,
      change: '+8%',
      changeType: 'positive' as const,
      icon: UsersIcon,
    },
    {
      name: '运营商数量',
      value: stats?.data?.total_operators || 0,
      change: '+2%',
      changeType: 'positive' as const,
      icon: UserGroupIcon,
    },
    {
      name: '待审核提现',
      value: stats?.data?.pending_withdrawals || 0,
      change: '-5%',
      changeType: 'negative' as const,
      icon: BanknotesIcon,
    },
  ]

  if (isLoading) {
    return (
      <div className="page-content">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="card">
                <div className="card-body">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="page-title">仪表板</h1>
        <p className="page-subtitle">
          欢迎回来，这里是系统概览
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {statsCards.map((item) => (
          <div key={item.name} className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <item.icon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {item.name}
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {item.value.toLocaleString()}
                      </div>
                      <div
                        className={`ml-2 flex items-baseline text-sm font-semibold ${
                          item.changeType === 'positive'
                            ? 'text-success-600'
                            : 'text-danger-600'
                        }`}
                      >
                        {item.change}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts and tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent activity */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">
              最近活动
            </h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">
                      用户 user{i + 1}@example.com 提交了提现申请
                    </p>
                    <p className="text-xs text-gray-500">
                      {i + 1} 分钟前
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* System status */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">
              系统状态
            </h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">数据库</span>
                <span className="badge-success">正常</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">API服务</span>
                <span className="badge-success">正常</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">实时推送</span>
                <span className="badge-success">正常</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">行情服务</span>
                <span className="badge-warning">延迟</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}